import { defineStore } from 'pinia';
import { ref } from 'vue';
import { identityServerClient } from '../sdk/identityServerClient.js';

/**
 * The person's authenticator app, as the Sign-in & security page sets it up: whether it is on,
 * whether this sign-in can manage it, and the secret to scan between starting and finishing.
 * Every refusal is kept as the server's own word in `error` (`SIGN_IN_AGAIN`, `MFA_CODE_WRONG`,
 * `MFA_NOT_AVAILABLE`), so the page can say what to do about it.
 */
export const mfaStore = defineStore('mfaStore', () => {
  const available = ref(false);
  const enabled = ref(false);
  const setup = ref(null);
  const loading = ref(false);
  const error = ref(null);

  async function guarded(work) {
    loading.value = true;
    error.value = null;
    try {
      return await work();
    } catch (e) {
      error.value = e?.body?.error || 'MFA_REQUEST_FAILED';
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function refresh() {
    const status = await guarded(() => identityServerClient.mfaStatus());
    if (status !== null) {
      available.value = status.available;
      enabled.value = status.enabled;
    }
  }

  // Resolves true when there is a secret to show.
  async function begin() {
    setup.value = await guarded(() => identityServerClient.beginMfa());
    return setup.value !== null;
  }

  // Resolves true when the app is on; false leaves the secret up, so the person can try the next code.
  async function enable(code) {
    const answer = await guarded(() => identityServerClient.enableMfa(code));
    if (answer === null) {
      return false;
    }
    enabled.value = answer.enabled;
    setup.value = null;
    return true;
  }

  async function disable() {
    const answer = await guarded(() => identityServerClient.disableMfa());
    if (answer !== null) {
      enabled.value = answer.enabled;
    }
  }

  // The secret is dropped, not sent anywhere: nothing was turned on, so nothing needs undoing.
  function cancelSetup() {
    setup.value = null;
    error.value = null;
  }

  return { available, enabled, setup, loading, error, refresh, begin, enable, disable, cancelSetup };
});
